import {
	FloatingFocusManager,
	FloatingPortal,
	useMergeRefs,
} from "@floating-ui/react";
import {
	type HTMLProps,
	type PropsWithChildren,
	createContext,
	forwardRef,
	useContext,
} from "react";
import classes from "./popover.module.css";
import {
	type UsePopoverProps,
	type UsePopoverState,
	usePopover,
} from "./use-popover";

export const PopoverStateContext = createContext<UsePopoverState>(
	undefined as never as UsePopoverState,
);

export const usePopoverState = () => useContext(PopoverStateContext);

export type PopoverProps = PropsWithChildren<UsePopoverProps>;

export function Popover({ children, ...props }: PopoverProps) {
	const popover = usePopover(props);
	return (
		<PopoverStateContext.Provider value={popover}>
			{children}
		</PopoverStateContext.Provider>
	);
}

export const PopoverButton = forwardRef<
	HTMLButtonElement,
	HTMLProps<HTMLButtonElement>
>((props, propsRef) => {
	const popover = usePopoverState();
	const ref = useMergeRefs([popover.floating.refs.setReference, propsRef]);
	return (
		<button
			{...props}
			ref={ref}
			type="button"
			onClick={popover.toggleOpen}
			{...popover.interactions.getReferenceProps()}
		>
			{props.children}
		</button>
	);
});

export const PopoverPanel = forwardRef<
	HTMLDivElement,
	HTMLProps<HTMLDivElement> & { lazy?: boolean }
>((props, propsRef) => {
	const popover = usePopoverState();
	const context = popover.floating.context;
	const ref = useMergeRefs([popover.floating.refs.setFloating, propsRef]);
	return (
		<FloatingPortal>
			<FloatingFocusManager context={context}>
				<div
					className={classes.panel}
					{...props}
					ref={ref}
					style={{ ...context.floatingStyles, ...props.style }}
					hidden={!popover.open}
					{...popover.interactions.getFloatingProps()}
				>
					{props.children}
				</div>
			</FloatingFocusManager>
		</FloatingPortal>
	);
});
